import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { TableSkeleton } from "@/components/base/TableSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { fmtDateTime } from "@/lib/format";
import { friendlyError } from "@/lib/errors";
import {
  defaultOperationalStatusFilter,
  operationalIssueAction,
} from "@/lib/operational-issues";
import {
  emptyOperationalIssueCatalog,
  presentOperationalIssue,
  type OperationalIssueCatalog,
} from "@/lib/operational-issue-ux";
import { matchesOperatorSearch } from "@/lib/sync-events-ux";

interface ExceptionRow {
  id: string;
  source: string;
  type: string;
  severity: string;
  status: string;
  entity_type: string;
  entity_id: string | null;
  message: string;
  first_seen: string;
  last_seen: string;
  occurrence_count: number;
  provider: string | null;
  action_hint: string;
}

const sourceColors: Record<string, string> = {
  integration: "bg-slate-100 text-slate-700",
  shipping: "bg-slate-100 text-slate-700",
  listing: "bg-amber-100 text-amber-700",
  provider: "bg-rose-100 text-rose-700",
  anomaly: "bg-emerald-100 text-emerald-700",
  automation: "bg-orange-100 text-orange-700",
};

const resolvableSources = new Set(["anomaly", "integration", "shipping", "listing", "automation"]);

const typeLabels: Record<string, string> = {
  unmapped_product: "Unmapped product",
  buyer_cancellation_requested: "Buyer requested cancellation",
  buyer_return_opened: "Buyer opened a return",
  buyer_item_not_received: "Buyer reports item not received",
  buyer_item_not_as_described: "Buyer reports item not as described",
  ebay_case_requires_attention: "eBay case requires attention",
  payment_dispute_requires_attention: "Payment dispute requires attention",
  inbound_family_poll_blocked: "Marketplace after-sales poll unavailable",
};

function TechnicalDetails({ fields }: { fields: { label: string; value: string }[] }) {
  return (
    <details className="mt-1">
      <summary className="text-[11px] text-slate-400 cursor-pointer hover:text-slate-600">Details</summary>
      <dl className="mt-1 space-y-0.5 text-[11px] text-slate-400 font-mono break-all">
        {fields.map((f) => (
          <div key={f.label}>
            <dt className="inline text-slate-300">{f.label}: </dt>
            <dd className="inline">{f.value}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

function entityLink(entityType: string, entityId: string | null): string | null {
  if (!entityId) return null;
  switch (entityType) {
    case "product":
      return `/products/${entityId}`;
    case "sales_order":
      return `/sales-orders/${entityId}`;
    case "listing":
      return `/listings/${entityId}`;
    case "shipment":
      return `/fulfilment/shipments/${entityId}`;
    default:
      return null;
  }
}

export default function ExceptionCentre() {
  const { org } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>(defaultOperationalStatusFilter());
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [detecting, setDetecting] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["exceptions", org?.id, sourceFilter, statusFilter, severityFilter],
    enabled: !!org?.id,
    queryFn: async () => {
      const { data: rows, error } = await supabase.rpc("get_unified_exceptions", {
        p_org_id: org!.id,
        p_source: sourceFilter === "all" ? null : sourceFilter,
        p_status: statusFilter,
        p_severity: severityFilter === "all" ? null : severityFilter,
        p_limit: 200,
      });
      if (error) throw error;
      const list = (rows ?? []) as ExceptionRow[];
      const catalog = emptyOperationalIssueCatalog();
      const extrasById: Record<string, { entity_ref: string | null; connector_id: string | null; issue_fingerprint: string | null; external_data: Record<string, unknown> | null }> = {};
      const [{ data: connectors }] = await Promise.all([
        supabase.from("connectors").select("id, name, provider").eq("org_id", org!.id),
      ]);
      for (const c of connectors ?? []) {
        catalog.connectors[c.id] = { id: c.id, name: c.name, provider: c.provider };
      }
      const integIds = list.filter((r) => r.source === "integration").map((r) => r.id);
      if (integIds.length) {
        const { data: extras } = await supabase
          .from("integration_exceptions")
          .select("id, entity_ref, connector_id, issue_fingerprint, external_data")
          .eq("org_id", org!.id)
          .in("id", integIds);
        const skus: string[] = [];
        for (const row of extras ?? []) {
          extrasById[row.id] = {
            entity_ref: row.entity_ref as string | null,
            connector_id: row.connector_id as string | null,
            issue_fingerprint: row.issue_fingerprint as string | null,
            external_data: (row.external_data as Record<string, unknown> | null) ?? null,
          };
          if (row.entity_ref) skus.push(String(row.entity_ref));
        }
        if (skus.length) {
          const { data: variants } = await supabase
            .from("variants")
            .select("id, product_id, sku, option_values")
            .eq("org_id", org!.id)
            .in("sku", [...new Set(skus)]);
          const productIds: string[] = [];
          for (const v of variants ?? []) {
            if (v.sku) {
              catalog.variantsBySku[String(v.sku)] = {
                id: v.id,
                product_id: v.product_id,
                sku: v.sku,
                option_values: (v.option_values as Record<string, string> | null) ?? null,
              };
            }
            if (v.product_id) productIds.push(String(v.product_id));
          }
          if (productIds.length) {
            const { data: products } = await supabase
              .from("products")
              .select("id, name")
              .eq("org_id", org!.id)
              .in("id", [...new Set(productIds)]);
            for (const p of products ?? []) catalog.productsById[p.id] = { id: p.id, name: p.name };
          }
        }
      }
      return { rows: list, catalog, extrasById };
    },
  });

  const runDetection = async () => {
    setDetecting(true);
    setActionMsg("");
    const { error } = await supabase.rpc("detect_operational_anomalies", { p_org_id: org!.id });
    setDetecting(false);
    if (error) setActionMsg(friendlyError(error.message));
    else {
      setActionMsg("Anomaly scan complete.");
      queryClient.invalidateQueries({ queryKey: ["exceptions", org?.id] });
    }
  };

  const setStatus = async (row: ExceptionRow, status: string) => {
    setPendingId(row.id);
    setActionMsg("");
    const { error } = await supabase.rpc("resolve_exception", { p_source: row.source, p_id: row.id, p_status: status });
    setPendingId(null);
    if (error) setActionMsg(friendlyError(error.message));
    else queryClient.invalidateQueries({ queryKey: ["exceptions", org?.id] });
  };

  const getAction = (row: ExceptionRow, entityRef?: string | null) =>
    operationalIssueAction({
      source: row.source,
      type: row.type,
      actionHint: row.action_hint,
      message: row.message,
      entityRef,
    });

  const presented = useMemo(() => {
    const catalog: OperationalIssueCatalog = data?.catalog ?? emptyOperationalIssueCatalog();
    return (data?.rows ?? []).map((row) => {
      const extra = data?.extrasById[row.id];
      const view = presentOperationalIssue({
        id: row.id,
        source: row.source,
        type: row.type,
        message: row.message,
        entityType: row.entity_type,
        entityId: row.entity_id,
        provider: row.provider,
        fingerprint: extra?.issue_fingerprint,
        entityRef: extra?.entity_ref,
        connectorId: extra?.connector_id,
        externalData: extra?.external_data,
        occurrenceCount: row.occurrence_count,
        catalog,
      });
      return { row, extra, view };
    });
  }, [data]);

  const visible = presented.filter((item) => matchesOperatorSearch(item.view.searchText, search));

  const filters = [
    { label: "Source", value: sourceFilter, options: ["all", "integration", "shipping", "listing", "anomaly", "provider", "automation"], setter: setSourceFilter },
    { label: "Status", value: statusFilter, options: ["actionable", "open", "acknowledged", "resolved", "ignored", "all"], setter: setStatusFilter },
    { label: "Severity", value: severityFilter, options: ["all", "critical", "error", "warning", "info"], setter: setSeverityFilter },
  ];

  return (
    <div>
      <PageHeader
        title="Operational Issues"
        subtitle="Current mapping, sync and warehouse exceptions — one active issue per unresolved condition"
      />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {filters.map((f) => (
          <div key={f.label} className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
            {f.options.map((opt) => (
              <button
                key={opt}
                onClick={() => f.setter(opt)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  f.value === opt ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {opt === "all" ? "All" : opt === "actionable" ? "Needs attention" : opt.charAt(0).toUpperCase() + opt.slice(1)}
              </button>
            ))}
          </div>
        ))}
        <div className="ml-auto flex items-center gap-3">
          {actionMsg && <span className="text-xs text-slate-500">{actionMsg}</span>}
          <button
            onClick={runDetection}
            disabled={detecting}
            className="px-3 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-radar-line" />
            {detecting ? "Scanning..." : "Scan for anomalies"}
          </button>
        </div>
      </div>

      <div className="mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          placeholder="Search product, SKU, order number, connector, or issue type"
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8"><TableSkeleton /></div>
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-500 mb-4">Couldn't load exceptions.</p>
            <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
          </div>
        ) : visible.length === 0 ? (
          <EmptyState icon="ri-shield-check-line" title="No issues in this view" description="Open and acknowledged exceptions appear here by default. Resolved and ignored history is under All / Resolved / Ignored." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Exception</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Entity</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Message</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">First seen</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last seen</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(({ row, extra, view }) => {
                  const action = getAction(row, row.action_hint === "review_sales_order" ? row.entity_id : extra?.entity_ref);
                  const link = entityLink(row.entity_type, row.entity_id);
                  return (
                    <tr key={`${row.source}-${row.id}`} className="border-b border-slate-50 hover:bg-slate-50/40 align-top">
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${sourceColors[row.source] ?? "bg-slate-100 text-slate-600"}`}>
                          {row.source}
                        </span>
                        <p className="text-[10px] text-slate-400 mt-0.5">{view.channelLine}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-800">{typeLabels[row.type] ?? row.type.replace(/_/g, " ")}</p>
                        <p className="text-xs text-slate-400">{row.severity}{row.occurrence_count > 1 ? ` · ${row.occurrence_count} observations` : ""}</p>
                      </td>
                      <td className="px-4 py-3">
                        {link ? (
                          <button onClick={() => navigate(link)} className="text-left">
                            <p className="text-sm font-medium text-slate-800">{view.headline}</p>
                          </button>
                        ) : (
                          <p className="text-sm font-medium text-slate-800">{view.headline}</p>
                        )}
                        {view.secondary.map((line) => (
                          <p key={line} className="text-xs text-slate-500 mt-0.5">{line}</p>
                        ))}
                        <TechnicalDetails fields={view.technical} />
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-xs">
                        <p>{view.messageTitle}</p>
                        {view.messageDetail && <p className="text-xs text-slate-500 mt-0.5">{view.messageDetail}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400">{fmtDateTime(row.first_seen)}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{fmtDateTime(row.last_seen)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          {action.path && (
                            <button
                              onClick={() => navigate(action.path!)}
                              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                            >
                              {action.label}
                            </button>
                          )}
                          {resolvableSources.has(row.source) && (
                            <>
                              {row.status === "open" && (
                                <button
                                  onClick={() => setStatus(row, "acknowledged")}
                                  disabled={pendingId === row.id}
                                  title="Acknowledge"
                                  className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-50 text-slate-500 disabled:opacity-50"
                                >
                                  <i className="ri-check-line text-sm" />
                                </button>
                              )}
                              {row.status !== "resolved" && (
                                <button
                                  onClick={() => setStatus(row, "resolved")}
                                  disabled={pendingId === row.id}
                                  title="Resolve"
                                  className="w-7 h-7 flex items-center justify-center rounded-md border border-emerald-200 hover:bg-emerald-50 text-emerald-600 disabled:opacity-50"
                                >
                                  <i className="ri-check-double-line text-sm" />
                                </button>
                              )}
                              {row.status !== "ignored" && (
                                <button
                                  onClick={() => setStatus(row, "ignored")}
                                  disabled={pendingId === row.id}
                                  title="Ignore"
                                  className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-50 text-slate-400 disabled:opacity-50"
                                >
                                  <i className="ri-forbid-2-line text-sm" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
